import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import RecipeCard from "@/components/RecipeCard";
import { Search } from "lucide-react";

const Recipes = () => {
    const [recipes, setRecipes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [q, setQ] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const activeCategory = searchParams.get("category") || "All";
    const [origin, setOrigin] = useState("All");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([api.get("/recipes"), api.get("/categories")])
            .then(([r, c]) => {
                setRecipes(r.data);
                setCategories(["All", ...c.data.categories]);
            })
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        return recipes.filter((r) => {
            const matchOrigin = origin === "All" || r.origin === origin;
            const matchCategory =
                activeCategory === "All" || (r.categories || []).includes(activeCategory);
            const matchQ =
                !q ||
                r.title.toLowerCase().includes(q.toLowerCase()) ||
                r.short_description.toLowerCase().includes(q.toLowerCase()) ||
                (r.backstory || "").toLowerCase().includes(q.toLowerCase());
            return matchOrigin && matchCategory && matchQ;
        });
    }, [recipes, q, origin, activeCategory]);

    const setCategory = (c) => {
        if (c === "All") setSearchParams({});
        else setSearchParams({ category: c });
    };

    return (
        <div data-testid="recipes-page" className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-28">
            <div className="max-w-3xl hl-fade-up">
                <p className="font-script text-3xl text-hl-secondary">the kitchen archive —</p>
                <h1 className="font-serif text-5xl md:text-7xl text-hl-brown mt-2 leading-[1.02]">
                    All the recipes,<br />with their <span className="italic">stories</span>
                </h1>
                <p className="mt-6 text-hl-muted text-lg leading-relaxed max-w-2xl">
                    Every dish in our archive comes with the history that made it —
                    where it came from, who first cooked it, and why it still belongs
                    on a Greek or Cypriot table.
                </p>
            </div>

            {/* Filters */}
            <div data-testid="recipes-filters" className="mt-14 space-y-6">
                <div className="flex flex-col md:flex-row gap-4 md:items-center">
                    <div className="relative flex-1 w-full md:max-w-md">
                        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-hl-muted" strokeWidth={1.5} />
                        <input
                            data-testid="recipes-search"
                            placeholder="Search recipes..."
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 rounded-sm border border-hl-border bg-hl-surface text-hl-text placeholder:text-hl-muted/70 focus:outline-none focus:border-hl-primary transition-colors"
                        />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {["All", "Greece", "Cyprus"].map((o) => (
                            <button
                                key={o}
                                data-testid={`filter-origin-${o.toLowerCase()}`}
                                onClick={() => setOrigin(o)}
                                className={`px-5 py-2 rounded-sm text-[11px] uppercase tracking-[0.25em] hl-btn-lift border ${
                                    origin === o
                                        ? "bg-hl-brown text-hl-bg border-hl-brown"
                                        : "bg-transparent text-hl-text border-hl-border hover:border-hl-brown"
                                }`}
                            >
                                {o}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    {categories.map((c) => (
                        <button
                            key={c}
                            data-testid={`filter-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => setCategory(c)}
                            className={`px-4 py-1.5 text-[10px] uppercase tracking-[0.25em] rounded-full border transition-colors ${
                                activeCategory === c
                                    ? "bg-hl-secondary text-hl-bg border-hl-secondary"
                                    : "border-hl-border text-hl-muted hover:border-hl-secondary hover:text-hl-secondary"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </div>

            {/* Grid */}
            <div className="mt-16">
                {loading ? (
                    <p data-testid="recipes-loading" className="text-hl-muted font-serif text-2xl italic">Loading the archive...</p>
                ) : filtered.length === 0 ? (
                    <p
                        data-testid="recipes-empty"
                        className="text-hl-muted italic py-24 text-center font-serif text-2xl"
                    >
                        No recipes match. Try a different word, region, or category.
                    </p>
                ) : (
                    <div
                        data-testid="recipes-grid"
                        className="grid gap-12 md:gap-10 sm:grid-cols-2 lg:grid-cols-3"
                    >
                        {filtered.map((r, i) => (
                            <RecipeCard key={r.id} recipe={r} index={i} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Recipes;
